<script setup lang="ts">
import { MoveUpRightIcon, XIcon } from 'lucide-vue-next'
import { computed } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import { colorMap } from '~/utils/badge'

const {
  color = 'neutral',
  to,
  text,
  removable,
  size = 'sm',
  icon,
  outline = false,
} = defineProps<{
  color?: keyof typeof colorMap
  to?: RouteLocationRaw
  text?: string
  icon?: Component
  removable?: boolean
  size?: 'xs' | 'sm' | 'md' | 'lg'
  outline?: boolean
}>()

const emit = defineEmits<{
  (e: 'removed', text: string): void
}>()

const router = useRouter()

const backgroundColor = computed(() => {
  if (outline) {
    return ''
  }
  return color ? colorMap[color].bg : ''
})

const textColor = computed(() => {
  if (outline) {
    return ''
  }
  return color ? colorMap[color].text : ''
})

const hoverColor = computed(() => {
  return to && color ? colorMap[color].hover : ''
})

const sizeClass = computed(() => {
  return {
    'badge-xs text-xs': size === 'xs',
    'badge-sm text-xs': size === 'sm',
    'badge-md text-xs': size === 'md',
    'badge-lg text-sm': size === 'lg',
  }
})

function remove() {
  emit('removed', text ?? '')
}

function goTo(e: MouseEvent) {
  if (to) {
    e.stopPropagation()
    router.push(to)
  }
}
</script>

<template>
  <span
    v-if="text || $slots.default"
    class="inline-flex items-center justify-center
      whitespace-nowrap gap-2 group/badge badge"
    :class="[
      backgroundColor,
      textColor,
      hoverColor,
      $attrs.class,
      sizeClass,
      { 'badge-outline': outline },
      { 'cursor-pointer': to || removable },
    ]"
    @click="goTo"
  >
    <component
      :is="icon"
      v-if="icon"
      :size="12"
    />

    <slot>
      <span class="truncate">{{ text }}</span>
    </slot>

    <XIcon
      v-if="removable"
      :size="12"
      class="text-base-content/50"
      @click="remove"
    />

    <MoveUpRightIcon
      v-if="to"
      :stroke-width="3"
      :size="12"
      class="text-base-content/50 group-hover/badge:text-primary-content"
    />
  </span>
</template>
